import { getApplicationPathTagsPublicRemote } from '@flex-apis/recruiting';
import { Select, TextField } from '@flex-design-system/fx';
import {
  ChevronDownMiniIcon,
  FlagIcon,
} from '@flex-design-system/legacy-flex-icons';
import { Translation, useTranslation } from '@flex-packages/i18n';
import { trans } from '@flex-packages/i18n-base';
import { useQueryFromRemote } from '@flex-packages/remote-hooks';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import type { JobDescriptionFormState } from '../models';

interface Props {
  customerIdHash: string;
}

export function ApplicationPathField(props: Props) {
  const { customerIdHash } = props;
  const { t } = useTranslation();
  const { control, clearErrors } = useFormContext<JobDescriptionFormState>();
  const { data: applicationPathTags = [] } = useQueryFromRemote({
    remote: getApplicationPathTagsPublicRemote,
    extraParams: {
      customerIdHash,
    },
    options: { suspense: false },
  });

  const [isOpen, setIsOpen] = useState(false);

  return (
    <Controller
      key="applicationPath"
      control={control}
      name="applicationPath"
      rules={{
        required: t(
          trans(
            'recruiting.job_description.application_path.edit.helper.required'
          )
        ),
      }}
      render={({
        field: { ref, onChange, value },
        fieldState: { invalid, error },
      }) => {
        return (
          <Select
            open={isOpen}
            onOpenChange={setIsOpen}
            value={value}
            onValueChange={newValue => {
              onChange(newValue);
              clearErrors('applicationPath');
            }}
          >
            <Select.Trigger type={undefined} asChild={true}>
              {/** onFocus재호출 방지 */}
              <div role="button">
                <TextField
                  ref={ref}
                  key={value}
                  defaultValue={value}
                  variant="solid"
                  id="applicationPath"
                  enterKeyHint="next"
                  required
                  css={{ cursor: 'pointer' }}
                  onFocus={() => {
                    setIsOpen(true);
                  }}
                  rightSlot={
                    <ChevronDownMiniIcon style={{ marginRight: 12 }} />
                  }
                  label={
                    <TextField.Label leftSlot={<FlagIcon />}>
                      <Translation
                        tKey={trans(
                          'recruiting.job_description.application_path'
                        )}
                      />
                    </TextField.Label>
                  }
                  placeholder={t(
                    trans(
                      'recruiting.job_description.application_path.edit.placeholder'
                    )
                  )}
                  state={invalid ? 'error' : 'default'}
                  helperText={
                    <TextField.HelperText>
                      {error?.message}
                    </TextField.HelperText>
                  }
                />
              </div>
            </Select.Trigger>
            <Select.Content>
              <Select.Group
                title={
                  <Select.GroupTitle>
                    <Translation
                      tKey={trans(
                        'recruiting.job_description.application_path'
                      )}
                    />
                  </Select.GroupTitle>
                }
              >
                {applicationPathTags.map(tag => (
                  <Select.Option
                    key={tag.applicationPathTagIdHash}
                    value={tag.name}
                  >
                    <Select.OptionTitle>{tag.name}</Select.OptionTitle>
                  </Select.Option>
                ))}
              </Select.Group>
            </Select.Content>
          </Select>
        );
      }}
    />
  );
}
